import { css } from 'aphrodite';
import * as React from 'react';
import { bs } from '../../../styles/styles';
import { TextInput } from '../../../components/TextInput';
import { InputFieldError } from '../../../components/InputFieldError';
import { styleSheet } from '../../../components/automation/AutomationStepCard/styles';

interface HtmlNewsletterStepFormProps {
	/**
	 * Current name/description of the newsletter step.
	 */
	name: string;
	/**
	 * Whether the newsletter has content/template selected.
	 */
	hasContent: boolean;
	/**
	 * Whether the form can be edited.
	 */
	canEdit: boolean;
	/**
	 * Whether the template picker/preview can be opened.
	 */
	canOpenTemplate: boolean;
	/**
	 * CTA button label.
	 */
	buttonText: string;
	/**
	 * Whether data is being saved.
	 */
	isBusy: boolean;
	/**
	 * Unique key for the form.
	 */
	baseKey: string;
	/**
	 * Error message to display (if any).
	 */
	errorMessage?: string | null;
	/**
	 * Callback when name changes.
	 */
	onNameChange: (name: string) => void;
	/**
	 * Callback when name input blurs (trigger save).
	 */
	onNameBlur: () => void;
	/**
	 * Callback to open the template selection modal.
	 */
	onSelectTemplate: () => void;
	/**
	 * Callback to clear error messages.
	 */
	onClearError: () => void;
}

/**
 * Form component for editing HTML newsletter automation step content.
 * Displays name input and template selection button.
 */
export function HtmlNewsletterStepForm({
	name,
	hasContent,
	canEdit,
	canOpenTemplate,
	buttonText,
	isBusy,
	baseKey,
	errorMessage,
	onNameChange,
	onNameBlur,
	onSelectTemplate,
	onClearError,
}: HtmlNewsletterStepFormProps) {
	const isDisabled = !canEdit || isBusy;

	const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		onNameChange(e.target.value);
		onClearError();
	};

	return (
		<>
			<div className={css(styleSheet.fieldLabel)}>
				HTML Newsletter: &nbsp;
				<span className={css(bs.required)}>*</span>
			</div>
			<TextInput
				className={css(styleSheet.emailNameInput)}
				disabled={isDisabled}
				inputId={`automation-newsletter-step-name-input-${baseKey}`}
				key={`${baseKey}-name-input`}
				onBlur={onNameBlur}
				onChange={handleNameChange}
				onFocus={onClearError}
				placeholder='Enter a brief description of this newsletter'
				type='text'
				value={name || ''}
			/>
			<InputFieldError errorMessage={errorMessage}>
				<button
					className={css(styleSheet.emailCta, !hasContent ? styleSheet.emailCreateButton : styleSheet.emailEditButton)}
					disabled={!canOpenTemplate || isBusy}
					onClick={onSelectTemplate}
				>
					<span>{buttonText}</span>
				</button>
			</InputFieldError>
		</>
	);
}
